import axios from 'axios';
import { SELECTED_CLIENT_ID_KEY, VIEW_AS_CLIENT_KEY } from '@/viewAsClient';
import { getAccessToken } from '@/services/auth/tokenService';

export const REST_API_BASE_URL = import.meta.env.VITE_SCP_API_URL;

const axiosLocal = axios.create({
    baseURL: REST_API_BASE_URL,
});

const nullInjectorExeptionList: string[] = [];

export const nullInjector = (req: any) => {
    if (nullInjectorExeptionList.includes(req.path)) {
        return req;
    }

    if (req && req.method !== 'get' && req.data) {
        // @ts-ignore
        req.data = JSON.parse(JSON.stringify(req.data).replaceAll('\\"\\"', null));
    }

    return req;
};

const trimStringValues = (data: any): any => {
    if (typeof data === 'string') {
        if (data.trim().startsWith('{') && data.trim().endsWith('}')) {
            try {
                const parsedData = JSON.parse(data);
                const trimmedObject = trimStringValues(parsedData);
                return JSON.stringify(trimmedObject);
            } catch (e) {
                return data.trim();
            }
        }
        return data.trim();
    }

    if (Array.isArray(data)) {
        return data.map((item) => trimStringValues(item));
    }

    if (data !== null && typeof data === 'object') {
        const result: any = {};
        for (const key in data) {
            const trimmed = trimStringValues(data[key]);
            result[key] = trimmed;
        }
        return result;
    }

    return data;
};

const trimStringValuesInjector = (req: any) => {
    if (req && req.method !== 'get') {
        req.data = trimStringValues(req.data);
    }

    return req;
};

const dynamicUrlInjector = (config: any) => {
    if (config.headers && config.headers['X-Client-Id']) {
        const clientId = config.headers['X-Client-Id'];

        delete config.headers['X-Client-Id'];

        if (config.url && /^\/users/.test(config.url)) {
            config.url = config.url.replace('/users', `/clients/${clientId}/users`);
        }
        if (config.url && /^\/roles/.test(config.url)) {
            config.url = config.url.replace('/roles', `/clients/${clientId}/roles`);
        }
        if (config.url && /^\/webhooks/.test(config.url)) {
            config.url = config.url.replace('/webhooks', `/clients/${clientId}/webhooks`);
        }
        if (config.url && /^\/api-keys/.test(config.url)) {
            config.url = config.url.replace('/api-keys', `/clients/${clientId}/api-keys`);
        }
        if (config.url && /^\/cards/.test(config.url)) {
            config.url = config.url.replace('/cards', `/clients/${clientId}/cards`);
        }
        if (config.url && /^\/orders/.test(config.url)) {
            config.url = config.url.replace('/orders', `/clients/${clientId}/orders`);
        }
        if (config.url && /^\/payments/.test(config.url)) {
            config.url = config.url.replace('/payments', `/clients/${clientId}/payments`);
        }
        if (config.url && /^\/customer-payments/.test(config.url)) {
            config.url = config.url.replace(
                '/customer-payments',
                `/clients/${clientId}/customer-payments`,
            );
        }
        if (config.url === '/ibans') {
            config.url = `/clients/${clientId}/ibans`;
        }
        if (config.url && /^\/transactions/.test(config.url)) {
            config.url = config.url.replace('/transactions', `/clients/${clientId}/transactions`);
        }
        if (config.url && /^\/card-shares/.test(config.url)) {
            config.url = config.url.replace('/card-shares', `/clients/${clientId}/card-shares`);
        }
        if (config.url && /^\/config/.test(config.url)) {
            config.url = config.url.replace('/config', `/clients/${clientId}/config`);
        }
        if (config.url && /^\/graphql/.test(config.url)) {
            config.url = config.url.replace('/graphql', `/clients/${clientId}/graphql`);
        }
    }
    return config;
};

axiosLocal.interceptors.request.use(nullInjector, (error) => Promise.reject(error));
axiosLocal.interceptors.request.use(trimStringValuesInjector, (error) => Promise.reject(error));
axiosLocal.interceptors.request.use(dynamicUrlInjector);

let accessTokenInterceptorAdded = false;

/**
 * Signs every request with a fresh access token.
 *
 * Axios keeps interceptors until they are ejected, so the flag stops repeated calls from
 * stacking duplicates that would each request a token for the same request.
 */
export const addAccessTokenInterceptor = () => {
    if (accessTokenInterceptorAdded) {
        return;
    }
    accessTokenInterceptorAdded = true;

    axiosLocal.interceptors.request.use(async (config) => {
        const token = await getAccessToken();
        config.headers['Authorization'] = `Bearer ${token}`;
        return config;
    });
};

export default axiosLocal;
